import * as React from "react";
import {createContext, useEffect, useState} from "react";
import {Route, Switch} from 'react-router-dom';
import {useArgoContext} from "@awsscm/argo-core";
import {ACCESS_DENIED_MESSAGE, DEFAULT_REASON_CODES, ERROR_MESSAGE, URLS} from "../util/ConfigConstants";
import {useLazyQuery} from "@apollo/react-hooks";
import {listEquipments} from "../gql/queries/list_equipments";
import {AppConfig, AppConfigMapKey} from "./config/AppConfig";
import {RouteLoadDurationProvider} from '@awsscm/argo-core/argo/utils/metrics/ArgoRouteLoadDurationContext';
import {credentialStorage} from '@awsscm/argo-core/argo/utils/CredentialStorage';
import Box from "@amzn/awsui-components-react-v3/polaris/box";
import {TableProps} from "@amzn/awsui-components-react-v3";
import {listShipmentConfigurations} from "../gql/queries/list_shipment_configurations";
import {isUserAccessDenied, isUserAccessDeniedMessage} from "../util/ExceptionUtil";
import {handleMultipleEquipments} from "../util/EquipmentUtil";
import CablingManifestPage from "./lmdp/cablingManifest/CablingManifestPage";
import {KatalHelper} from "./metrics/KatalHelper";
import ExternalUserNotAllowedPage from "./ExternalUserNotAllowedPage";
import {BULK_ACTION_REGISTRY} from './config/BulkActionRegistry';


// Lazy loading pages allows our build system to split up the code into different
// bundles, so customers do not have to download entire app at once. You can
// import these as normal if you do not need to split up code bundles between pages.
// Learn more https://reactjs.org/docs/code-splitting.html#route-based-code-splitting
const UploadAsnPage = React.lazy(() => import('./UploadAsnPage'));
const CreateBookingPage = React.lazy(() => import('./CreateBookingPage'));
const DetailPage = React.lazy(() => import('./DetailPage'));
const EditBooking = React.lazy(() => import('./EditAsnPage'));
const ShipmentEventsPage = React.lazy(() => import('./ShipmentEventsPage'));
const ShipmentAuditHistoryPage = React.lazy( () => import('./shipmentAuditHistory/ShipmentAuditHistory'));
const SearchPage = React.lazy(() => import('./searchpage'));
const DmsSearchPage = React.lazy(() => import('./dms/search'));
const DmsSiteLoadPage = React.lazy(() => import('./dms/siteContainerLoad'));
const DmsDetailsPage = React.lazy(() => import('./dms/details'));
const DmsSiteSummaryPage = React.lazy(() => import('./dms/siteContainerSummary'));
const ComingSoonPage = React.lazy(() => import('./ComingSoonPage'));
const SearchErrorDataPage = React.lazy(() => import('./SearchError'));
const PlannedDeliveriesPage = React.lazy(() => import('./lmdp/plannedDeliveries/PlannedDeliveriesPage'));
const EightWeekForecastPage = React.lazy(() => import('./lmdp/eightWeekForecast'));
const BlockedDeliveriesPage = React.lazy(() => import('./lmdp/blockedDeliveries'));
const LastMileHubViewPage = React.lazy(() => import('./lmdp/lastMileHubView'));
const ShippingConfigurationPage = React.lazy(() => import('./Configuration/Common'));
const EditShippingConfigurationPage = React.lazy(() => import('./Configuration/Edit'));
const ShipmentPlanSimulationsPage = React.lazy(() => import('./simulation'));
const PlannerAuditHistoryPage = React.lazy( () => import('./lmdp/PlannerAuditHistory/PlannerAuditHistory'));
const ShipmentsDownloadsPage = React.lazy(() => import('./downloads'));
const ShowAllDocumentsPage = React.lazy(() => import('./documentRepository/showAllDocumentView'));
const BulkActionPage = React.lazy(() => import('./bulkAction'));

/**
 * This interface would be used by App Layout Component to set global context.
 */
interface AppProps {
    setNotificationMessage: (message: React.ReactNode) => void
}

/**
 * This interface would be used by Detail Page, where we convert the Equipment Id to Equipment name.
 */
export interface EquipmentIdToEquipmentNameMappingResult {
    loading: boolean
    error?: string
    data?: Map<string, string>
}

/**
 * This interface would be used by Search Page, to display the dropdown with Vendor Id and Vendor Name for users to filter their queries.
 */
export interface VendorIdToVendorNameMappingResult {
    loading: boolean
    error?: string
    data?: Map<string, string>
}

/**
 * This interface would be used by Upload Asn page, where we provide dropdown to
 * the User to select Src and Dest loc with label as Equipment Name and id as Equipment Id.
 */
export interface EquipmentDataDropDownResult {
    loading: boolean
    error?: string
    data?: EquipmentDataDropDownInput[]
    loggedInVendors : string[]
}

export interface EquipmentDataDropDownInput {
    /**
     * Label string that gets rendered on the UI for the User to select
     */
    label: string
    /**
     * Upon selecting an option in the dropdown, corresponding Id would be
     * stored in the State of our UI, and would be passed to our backend. i.e. Equipment Id
     */
    id: string

    /**
     * This field will be required to filter out all the non-supplier nodes
     */
    equipmentVendorId?: string

    /**
     * This field will be required to filter out the non-DC nodes.
     */
    equipmentType?: string

    /**
     * This field will be required to filter out the locations that support booking shipment
     */
    isBookingSupported: boolean

    /**
     * This will be required to get the local timezone information in IANA format.
     */
    timezone: string

    /**
     * This will be required to get the zone associated with an equipment if its class is
     * SITE_V3 or SUPPLY_CHAIN_NODE_V1
     */
    shippingZone: string

    /**
     * This field is required to get the country associated with an equipment, in the 3-Letter-Format
     */
    country: string
}

export interface ShipmentsConfigDataDropDownResult {
    loading: boolean
    error?: string
    carrierData?: CarrierData[]
    carrierIdToName?:Map<string, string>
    programTypeData?: ProgramTypeData[]
    orderTypeData?: OrderTypeData[]
    reasonCodesData?: ReasonCodesData[]
}

export interface CarrierData {
    id: string,
    label: string,
    value: string
}

export interface ProgramTypeData {
    id: string,
    label: string,
    value: string
}

export interface OrderTypeData {
    id: string,
    label: string,
    value: string,
    applicableForBookingOrAsn: boolean
    description: string
}

export interface ReasonCodesData {
    id: string,
    label: string,
    value: string,
    metadata?: ReasonCodeMetadata[]
}

export interface ReasonCodeMetadata {
    state: string,
    category: string
}

export interface SearchSortDetails {
    sortingColumn: TableProps.SortingColumn<string>,
    sortingDescending: boolean,
    sortingPath: string,
    sortingProperty: string
}

export interface ShippingRequest {
  requestId: string
  clientRefId: string
  srcLocEquipmentId: string
  destLocEquipmentId: string
  carrier: string
  trackingNumber: string
  estdDeliveryTime?: string
  actualPickUpTime?: string
  orderType: string
  pickUpTime: {
    startTime: string,
    endTime:string
  }
  deliveryTime: {
    startTime: string,
    endTime:string
  }
  deliveryAccessId?: string
  deliveryInstructions?: string[]
  requestedEquipmentType: string
  incoterm: string
  modeOfTransport: string
  shippingSpeed: string
  paymentMethod: string
  bondedFlag?: string
  uploadedFileS3Uri: string
  classId: string
  state: string
  isAsnReceived?: boolean
}
export const KatalMetricsHelperContext = createContext<KatalHelper>({} as KatalHelper);
export default function (appProps: AppProps) {
    const shouldEnableUploadAsnPage = AppConfig.getConfigValue(AppConfigMapKey.ENABLE_UPLOAD_ASN);
    const shouldEnableCreateBookingPage = AppConfig.getConfigValue(AppConfigMapKey.ENABLE_CREATE_BOOKING);
    const shouldEnableSearchFeature = AppConfig.getConfigValue(AppConfigMapKey.ENABLE_SEARCH_FEATURE);
    const shouldEnableDms = AppConfig.getConfigValue(AppConfigMapKey.ENABLE_DMS);
    const shouldEnableShipmentEventsPage = AppConfig.getConfigValue(AppConfigMapKey.ENABLE_PACKAGING_DETAILS_CARD);
    const shouldEnableSearchErrorData = AppConfig.getConfigValue(AppConfigMapKey.ENABLE_SEARCH_ERROR_DATA);
    const shouldEnableLmdp = AppConfig.getConfigValue(AppConfigMapKey.ENABLE_LMDP);
    const shouldEnableLastMileHubView = AppConfig.getConfigValue(AppConfigMapKey.ENABLE_LAST_MILE_HUB_VIEW);
    const shouldEnableBlockedAndEightWeekDelivery = AppConfig.getConfigValue(AppConfigMapKey.ENABLE_BLOCKED_AND_EIGHT_WEEK_DELIVERY);
    const shouldEnableShippingConfigurations = AppConfig.getConfigValue(AppConfigMapKey.ENABLE_SHIPPING_CONFIGURATIONS);
    const shouldEnableShipmentDownloadsPage = AppConfig.getConfigValue(AppConfigMapKey.ENABLE_SHIPMENTS_DOWNLOAD_PAGE);
    const shouldEnablePlannerAuditHistory = AppConfig.getConfigValue(AppConfigMapKey.ENABLE_PLANNER_AUDIT_HISTORY);
    const shouldEnableDocumentRepositoryFeature : boolean = AppConfig.getConfigValue(AppConfigMapKey.ENABLE_DOCUMENT_REPOSITORY_FEATURE);
    const context = useArgoContext();
    const [listEquipmentsApi, listEquipmentsOutput] = useLazyQuery(listEquipments);
    const [shipmentsConfigDataDropDownResult, setShipmentsConfigDataDropDownResult] = useState<ShipmentsConfigDataDropDownResult>({
        loading: true,
        error: undefined
    });

    const [katalMetricsHelper, setKatalMetricsHelper] = useState(new KatalHelper(context)); // eslint-disable-line @typescript-eslint/no-unused-vars
    const [listShipmentConfigurationsApi] = useLazyQuery(listShipmentConfigurations, {
        fetchPolicy: "network-only",
        onCompleted: data => {
            if(data) {
                const carriersData = data.listShipmentConfigurations.carriers
                const programTypeData = data.listShipmentConfigurations.programTypes
                const orderTypeData = data.listShipmentConfigurations.orderTypes
                const reasonCodesData = data.listShipmentConfigurations.reasonCodes ?? DEFAULT_REASON_CODES
                const carrierIdToCarrierNameMapping = new Map<string, string>(carriersData.map((i:any) => [i.id, i.label]));
                setShipmentsConfigDataDropDownResult({carrierData: carriersData,
                    programTypeData: programTypeData, orderTypeData: orderTypeData, reasonCodesData: reasonCodesData, carrierIdToName: carrierIdToCarrierNameMapping, loading: false, error: undefined})
            } else {
                setShipmentsConfigDataDropDownResult({loading: false, error: ERROR_MESSAGE.GENERIC_INTERNAL_SERVER_ERROR_MESSAGE})
            }
        },
        onError: error => {
            if (isUserAccessDenied(error)) {
                setShipmentsConfigDataDropDownResult({loading: false, error: ERROR_MESSAGE.GENERIC_ACCESS_DENIED_ERROR_MESSAGE})
            } else {
                setShipmentsConfigDataDropDownResult({loading: false, error: error?.message})
            }
        }
    });
    useEffect(() => {
        listEquipmentsApi();
        listShipmentConfigurationsApi();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    let equipmentIdToEquipmentNameMappingResult: EquipmentIdToEquipmentNameMappingResult = {
        loading: listEquipmentsOutput.loading,
        error: listEquipmentsOutput.error?.message
    }
    if (listEquipmentsOutput.data) {
        const equipmentIdToEquipmentNameMapping = new Map<string, string>(listEquipmentsOutput.data.listEquipments.equipments.map((i: any) => [i.equipmentId, i.equipmentName]));
        equipmentIdToEquipmentNameMappingResult = {...equipmentIdToEquipmentNameMappingResult, data: equipmentIdToEquipmentNameMapping}
    }

    let vendorIdToVendorNameMappingResult: VendorIdToVendorNameMappingResult = {
        loading: listEquipmentsOutput.loading,
        error: listEquipmentsOutput.error?.message
    }
    if (listEquipmentsOutput.data) {
        const vendorIdToVendorNameMapping =
            new Map<string, string>(listEquipmentsOutput.data.listEquipments.equipments
                .filter((i: any) => i.equipmentVendorId && i.equipmentVendorName)
                .filter((i: any) => (!credentialStorage.isExternalUser()) || (listEquipmentsOutput.data.listEquipments.vendorIds && listEquipmentsOutput.data.listEquipments.vendorIds.includes(i.equipmentVendorId)))
                .map((i: any) => [i.equipmentVendorId, i.equipmentVendorName]));
        vendorIdToVendorNameMappingResult = {...vendorIdToVendorNameMappingResult, data: vendorIdToVendorNameMapping}
    }

    let equipmentDataDropDownResult: EquipmentDataDropDownResult = {
        loading: listEquipmentsOutput.loading,
        error: listEquipmentsOutput.error?.message,
        loggedInVendors: []
    }
    if (listEquipmentsOutput.data) {
        const equipmentDataDropDownInputs =
            listEquipmentsOutput.data.listEquipments.equipments
                .map((i: any) => ({label: i.equipmentName, id: i.equipmentId, value: i.equipmentId, equipmentVendorId: i.equipmentVendorId, equipmentType:i.equipmentType, isBookingSupported:i.isBookingSupported, timezone:i.timezone, shippingZone: i.shippingZone, country: i.country}));
        equipmentDataDropDownResult = {
            ...equipmentDataDropDownResult,
            data: handleMultipleEquipments(equipmentDataDropDownInputs),
            loggedInVendors: listEquipmentsOutput.data.listEquipments.vendorIds
        }
    }

    if(isUserAccessDeniedMessage(equipmentDataDropDownResult?.error) || isUserAccessDeniedMessage(shipmentsConfigDataDropDownResult?.error)) {
        appProps.setNotificationMessage(ACCESS_DENIED_MESSAGE);
    }

    return (
        // Suspense adds a loading page to display while loading the code.
        <KatalMetricsHelperContext.Provider value={katalMetricsHelper}>
        <React.Suspense fallback={<Box>Loading...</Box>}>
            <RouteLoadDurationProvider>
                <Switch>
                    <Route path={URLS.PAGES.PLANNED_DELIVERIES} render={
                        (props) => shouldEnableLmdp && !credentialStorage.isExternalUser() ? <PlannedDeliveriesPage equipmentDataDropDownResult={equipmentDataDropDownResult}/> : <ExternalUserNotAllowedPage/>}/>
                    <Route path={URLS.PAGES.THREE_WEEK_CABLING_FORECAST} render={
                        (props) =>  shouldEnableLmdp && !credentialStorage.isExternalUser() ? <CablingManifestPage equipmentDataDropDownResult={equipmentDataDropDownResult}/>: <ExternalUserNotAllowedPage/> }/>
                    <Route path={URLS.PAGES.DELIVERY_FORECAST} render={
                        (props) => !credentialStorage.isExternalUser() ? <EightWeekForecastPage equipmentDataDropDownResult={equipmentDataDropDownResult}/> : <ExternalUserNotAllowedPage/>}/>
                    <Route path={URLS.PAGES.BLOCKED_DELIVERIES} render={
                        (props) => shouldEnableBlockedAndEightWeekDelivery && !credentialStorage.isExternalUser() ? <BlockedDeliveriesPage equipmentDataDropDownResult={equipmentDataDropDownResult}/> : <ExternalUserNotAllowedPage/>}/>
                    <Route path={URLS.PAGES.LAST_MILE_HUB_VIEW} render={
                        (props) =>  shouldEnableLastMileHubView && !credentialStorage.isExternalUser() ? <LastMileHubViewPage equipmentDataDropDownResult={equipmentDataDropDownResult}/>: <ExternalUserNotAllowedPage/> }/>
                    <Route path={URLS.PAGES.SEARCH_ERROR_DATA} render={
                        (props) => shouldEnableSearchErrorData ? <SearchErrorDataPage/> :  <ComingSoonPage/>}/>
                    <Route path={URLS.PAGES.UPLOAD_ASN} render={
                        (props) => shouldEnableUploadAsnPage ? <UploadAsnPage {...props} argoContext={context} equipmentDataDropDownResult={equipmentDataDropDownResult} shipmentsConfigDataDropDownResult={shipmentsConfigDataDropDownResult} setNotificationMessage={appProps.setNotificationMessage}/> : <ComingSoonPage/>}/>
                    <Route path={URLS.PAGES.CREATE_BOOKING} render={
                        (props) => shouldEnableCreateBookingPage ? <CreateBookingPage {...props} argoContext={context} equipmentDataDropDownResult={equipmentDataDropDownResult} setNotificationMessage={appProps.setNotificationMessage}/> : <ComingSoonPage/>}/>
                    <Route path={'/details/:bookingId'} render={
                        (props) => <DetailPage {...props} equipmentIdToEquipmentNameMappingResult={equipmentIdToEquipmentNameMappingResult} equipmentDataDropDownResult={equipmentDataDropDownResult} shipmentsConfigDataDropDownResult={shipmentsConfigDataDropDownResult} setNotificationMessage={appProps.setNotificationMessage}/>} />
                    <Route path={'/:bookingId/tracking'} render={
                        (props) => shouldEnableShipmentEventsPage ? <ShipmentEventsPage {...props} equipmentDataDropDownResult={equipmentDataDropDownResult} setNotificationMessage={appProps.setNotificationMessage}/> : <ComingSoonPage/>} />
                    <Route path={'/audit/:bookingId'} render={
                        (props) => <ShipmentAuditHistoryPage {...props} equipmentIdToEquipmentNameMappingResult={equipmentIdToEquipmentNameMappingResult} equipmentDataDropDownResult={equipmentDataDropDownResult} />} />
                    <Route path={'/edit/:bookingId'} render={
                        (props) => shouldEnableUploadAsnPage ? <EditBooking {...props} argoContext={context} equipmentDataDropDownResult={equipmentDataDropDownResult} shipmentsConfigDataDropDownResult={shipmentsConfigDataDropDownResult} setNotificationMessage={appProps.setNotificationMessage}/> : <ComingSoonPage/>} />
                    <Route path={URLS.PAGES.DMS_SEARCH} render={
                        (props) => shouldEnableDms && !credentialStorage.isExternalUser() ? <DmsSearchPage equipmentIdToEquipmentNameMappingResult={equipmentIdToEquipmentNameMappingResult} equipmentDataDropDownResult={equipmentDataDropDownResult}/> : <ExternalUserNotAllowedPage/>}/>
                    <Route path={'/dms/booking/:bookingId/load/:stopId'} render={
                        (props) => shouldEnableDms && !credentialStorage.isExternalUser() ? <DmsSiteLoadPage {...props} argoContext={context} equipmentIdToEquipmentNameMappingResult={equipmentIdToEquipmentNameMappingResult} equipmentDataDropDownResult={equipmentDataDropDownResult} /> : <ExternalUserNotAllowedPage/>} />
                    <Route path={'/dms/booking/:bookingId/summary/:stopId'} render={
                        (props) => shouldEnableDms && !credentialStorage.isExternalUser() ? <DmsSiteSummaryPage {...props} equipmentIdToEquipmentNameMappingResult={equipmentIdToEquipmentNameMappingResult} argoContext={context} equipmentDataDropDownResult={equipmentDataDropDownResult} /> : <ExternalUserNotAllowedPage/>} />
                    <Route path={'/dms/booking/:bookingId'} render={
                        (props) => shouldEnableDms && !credentialStorage.isExternalUser() ? <DmsDetailsPage {...props} equipmentIdToEquipmentNameMappingResult={equipmentIdToEquipmentNameMappingResult} argoContext={context} equipmentDataDropDownResult={equipmentDataDropDownResult} /> : <ExternalUserNotAllowedPage/>}/>
                    <Route path={URLS.PAGES.SHIPPING_CONFIGURATION_EDIT_URL} render={(props) => shouldEnableShippingConfigurations && !credentialStorage.isExternalUser() ? <EditShippingConfigurationPage {...props} equipmentDataDropDownResult={equipmentDataDropDownResult} shipmentsConfigDataDropDownResult={shipmentsConfigDataDropDownResult}/> : <ExternalUserNotAllowedPage/>}/>
                    <Route path={URLS.PAGES.SHIPPING_CONFIGURATION_URL} render={(props) => shouldEnableShippingConfigurations && !credentialStorage.isExternalUser() ? <ShippingConfigurationPage {...props} equipmentDataDropDownResult={equipmentDataDropDownResult} shipmentsConfigDataDropDownResult={shipmentsConfigDataDropDownResult}/> : <ExternalUserNotAllowedPage/>}/>
                    <Route path={URLS.PAGES.SIMULATIONS} render={(props) => shouldEnableShippingConfigurations && !credentialStorage.isExternalUser() ? <ShipmentPlanSimulationsPage {...props} equipmentDataDropDownResult={equipmentDataDropDownResult} shipmentsConfigDataDropDownResult={shipmentsConfigDataDropDownResult}/> : <ExternalUserNotAllowedPage/>}/>
                    <Route path={URLS.PAGES.DOWNLOADS} render={(props) => shouldEnableShipmentDownloadsPage && !credentialStorage.isExternalUser() ? <ShipmentsDownloadsPage /> : <ExternalUserNotAllowedPage/>}/>
                    <Route path={'/auditHistory/pkgid/:id'} render={
                        (props) => shouldEnablePlannerAuditHistory && !credentialStorage.isExternalUser() ? <PlannerAuditHistoryPage {...props} /> : <ExternalUserNotAllowedPage/>} />
                    <Route path={'/:bookingId/:poNumber/:assetId/documents'} render={
                        (props) => shouldEnableDocumentRepositoryFeature ? <ShowAllDocumentsPage {...props} equipmentDataDropDownResult={equipmentDataDropDownResult}/> : <ComingSoonPage/> }/>
                    {BULK_ACTION_REGISTRY.map(action => (
                        <Route key={action.id} path={action.route} render={
                            (props) => !credentialStorage.isExternalUser()
                                ? (AppConfig.getConfigValue(action.flagKey)
                                    ? <action.component {...props} equipmentDataDropDownResult={equipmentDataDropDownResult}/>
                                    : <ComingSoonPage/>)
                                : <ExternalUserNotAllowedPage/>
                        }/>
                    ))}
                    <Route path={URLS.PAGES.BULK_ACTIONS} render={
                        (props) => !credentialStorage.isExternalUser() ? <BulkActionPage /> : <ExternalUserNotAllowedPage/>}/>
                    <Route path={URLS.PAGES.TRACK_SHIPMENTS} render={
                        (props) => shouldEnableSearchFeature ?
                            <SearchPage {...props}
                                        vendorIdToVendorNameMappingResult={vendorIdToVendorNameMappingResult}
                                        equipmentIdToEquipmentNameMappingResult={equipmentIdToEquipmentNameMappingResult}
                                        equipmentDataDropDownResult={equipmentDataDropDownResult}
                                        shipmentsConfigDataDropDownResult={shipmentsConfigDataDropDownResult}
                                        setNotificationMessage={appProps.setNotificationMessage}
                            /> : <ComingSoonPage/>}/>
                </Switch>
            </RouteLoadDurationProvider>
        </React.Suspense>
        </KatalMetricsHelperContext.Provider>
  );
}
